<template>
  <Teleport to="body">
    <div
      v-show="isMobileNavigationOpen"
      ref="containerRef"
      class="tw-fixed tw-inset-0 tw-z-[1000]"
    >
      <div class="tw-h-full tw-overflow-y-scroll tw-bg-white">
        <div class="tw-flex tw-flex-col md:tw-flex-row">
          <div
            class="tw-flex tw-flex-col tw-p-4 md:tw-w-1/2 md:tw-border-r md:tw-border-neutral-200 md:tw-pr-8"
          >
            <div
              v-for="(item, index) in navigation"
              :key="index"
              class="tw-space-y-1"
            >
              <InertiaLink
                v-if="!item.dropdown"
                :href="item.href[accessControl.actorType]"
                class="tw-inline-flex tw-items-center tw-p-3 tw-font-semibold hover:tw-text-current"
              >
                <component
                  :is="item.icon"
                  class="tw-mr-4 tw-h-6 tw-w-6 tw-stroke-[1.5px]"
                />
                {{ item.name }}
              </InertiaLink>

              <Disclosure v-else>
                <template
                  #activator="{ toggle, isActive, ariaExpanded, ariaControls }"
                >
                  <button
                    :aria-expanded="ariaExpanded"
                    :aria-controls="ariaControls"
                    @click="toggle"
                    class="tw-inline-flex tw-w-full tw-items-center tw-justify-between tw-p-3 tw-font-semibold hover:tw-text-current"
                  >
                    <span class="tw-inline-flex tw-items-center">
                      <component
                        :is="item.icon"
                        class="tw-mr-4 tw-h-6 tw-w-6 tw-stroke-[1.5px]"
                      />
                      {{ item.name }}
                    </span>
                    <ArrowRight
                      class="tw-ml-2 tw-w-2 tw-shrink-0 tw-transition-transform"
                      :class="[
                        isActive ? 'tw-rotate-[270deg]' : 'tw-rotate-90',
                      ]"
                    />
                  </button>
                </template>
                <template #content>
                  <div class="tw-flex tw-flex-col tw-space-y-1">
                    <InertiaLink
                      v-for="dropdownElement in item.dropdown"
                      :key="dropdownElement.name"
                      :href="dropdownElement.href[accessControl.actorType]"
                      class="tw-inline-flex tw-items-center tw-p-3 tw-font-semibold hover:tw-text-current"
                    >
                      <component
                        :is="dropdownElement.icon"
                        class="tw-mr-4 tw-h-6 tw-w-6 tw-stroke-[1.5px]"
                      />
                      {{ dropdownElement.name }}
                    </InertiaLink>
                  </div>
                </template>
              </Disclosure>
            </div>
            <div
              v-if="isUserLoggedIn"
              class="tw-mt-4 tw-flex tw-flex-col tw-space-y-2"
            >
              <BaseButton
                color="primary"
                size="large"
                is-block
                href="/search"
                is-inertia-link
              >
                Browse mentors
              </BaseButton>
            </div>
          </div>
          <div v-if="isUserLoggedIn" class="tw-p-4 md:tw-w-1/2 md:tw-pl-8">
            <div
              class="tw-text-xs tw-font-semibold tw-uppercase tw-text-neutral-500"
            >
              Account
            </div>
            <InertiaLink
              :href="currentUser.profileUrl"
              class="tw-mt-2 tw-flex tw-items-center tw-justify-between tw-space-x-6 tw-rounded-lg tw-p-3 hover:tw-bg-neutral-50"
            >
              <div class="tw-flex tw-items-center tw-space-x-3">
                <Avatar
                  :src="currentUser.avatarUrl"
                  :alt="`${currentUser.firstName} ${currentUser.lastName}`"
                  disable-lazy-loading
                  size="lg"
                  class="tw-flex-shrink-0"
                />
                <div class="tw-flex tw-flex-col">
                  <span class="tw-font-semibold">
                    {{ currentUser.firstName }} {{ currentUser.lastName }}
                  </span>
                  <span class="tw-text-xs tw-text-neutral-400">
                    view profile
                  </span>
                </div>
              </div>
              <BaseIcon
                name="arrow-sm-right"
                color="neutral-400"
                class="tw-size-4"
              />
            </InertiaLink>
            <div class="tw-mt-3 tw-flex tw-flex-col tw-space-y-1">
              <component
                v-for="(item, index) in userDropdownNavigation[
                  accessControl.role
                ]"
                v-bind="{ ...item.attrs, ...item.actions }"
                :is="item.as"
                :key="index"
                :href="item.isDisabled ? 'javascript:void(0)' : item.href"
                class="tw-p-2 tw-text-left tw-text-neutral-700 hover:tw-text-current"
                :class="{
                  'tw-cursor-not-allowed': item.isDisabled,
                }"
              >
                <div class="tw-inline-flex tw-items-center">
                  <component
                    :is="item.icon"
                    class="tw-mr-3 tw-h-6 tw-w-6 tw-stroke-[1.5px]"
                    :class="item.iconClasses"
                  />
                  <span class="tw-font-medium">{{ item.name }}</span>
                  <span
                    v-if="item.isNew"
                    class="tw-ml-1 tw-rounded-md tw-bg-success-100 tw-px-1 tw-py-0.5 tw-text-xs tw-text-success-600"
                  >
                    New
                  </span>
                </div>
                <span
                  v-if="item.description"
                  class="tw-mt-1 tw-flex tw-text-xs"
                  :class="{ 'tw-text-neutral-400': item.isDisabled }"
                >
                  {{ item.description }}
                </span>
              </component>
            </div>
          </div>
          <div
            v-else
            class="tw-flex tw-p-4 sm:tw-justify-center md:tw-mt-0 md:tw-w-1/2 md:tw-items-center md:tw-pl-8"
          >
            <div
              class="tw-flex tw-w-full tw-flex-col tw-space-y-2 xs:tw-flex-row xs:tw-space-x-2 xs:tw-space-y-0 md:tw-grow md:tw-flex-col md:tw-space-x-0 md:tw-space-y-4"
            >
              <BaseButton size="small" href="/search" is-block is-inertia-link>
                Browse mentors
              </BaseButton>
              <BaseButton
                color="outline-secondary"
                href="/mentees/sign_up"
                is-block
              >
                Sign up
              </BaseButton>
              <BaseButton color="primary" href="/users/sign_in" is-block>
                Log in
              </BaseButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import BaseButton from '../BaseButton.vue'
import Avatar from '../avatar/Avatar.vue'
import Disclosure from '@/shared/components/Disclosure.vue'
import ArrowRight from '@/assets/images/icons/arrow-right.svg'
import { ref, watch } from 'vue'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem.js'
import { useCurrentUser } from '@/shared/composables/useCurrentUser.js'
import BaseIcon from '@/shared/components/BaseIcon.vue'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  navigation: {
    type: Array,
    required: true,
  },
  userDropdownNavigation: {
    type: Object,
    default: () => ({}),
  },
})

const { isUserLoggedIn, accessControl } = usePermissionSystem()
const { currentUser } = useCurrentUser()

const isMobileNavigationOpen = ref(false)
const containerRef = ref(null)

watch(
  () => props.isOpen,
  (value) => {
    if (value) {
      lockBody()
      setContainerPosition()
      isMobileNavigationOpen.value = true
    } else {
      unlockBody()
      isMobileNavigationOpen.value = false
    }
  },
)

const lockBody = () => {
  document.body.classList.add('!tw-overflow-hidden')
}

const unlockBody = () => {
  document.body.classList.remove('!tw-overflow-hidden')
}

const setContainerPosition = () => {
  const main = document.getElementsByTagName('main')[0]
  // main.offsetTop is the distance from the top of the page to the top of the main element
  // (we want to keep everything above main visible while opening the mobile navigation)
  containerRef.value.style.height = `calc(100vh - ${main.offsetTop}px)`
  containerRef.value.style.marginTop = `${main.offsetTop}px`
}
</script>
